import { Alert, CircularProgress, Grid, Typography } from '@mui/material';
import { join, map, size } from 'lodash';
import React, { ReactElement } from 'react';

import { ExecutionRequirementState, ExecutionRequirementType } from './useExecutionRequirements';

export interface ExecutionRequirementsPanelProps {
  requirements: ExecutionRequirementState[];
}

const renderCloudOptimizedSlidesRequirement = (
  requirement: Extract<ExecutionRequirementState, { type: ExecutionRequirementType.CloudOptimizedSlides }>
) => {
  if (requirement.needsInput) {
    return (
      <Alert severity="info">
        <Typography>Select a stain to check cloud-optimized slide readiness.</Typography>
      </Alert>
    );
  }

  if (requirement.isLoading) {
    return (
      <Alert severity="info">
        <Grid container alignItems="center" spacing={1}>
          <Grid item>
            <CircularProgress size={18} />
          </Grid>
          <Grid item>
            <Typography>Checking cloud-optimized slide readiness.</Typography>
          </Grid>
        </Grid>
      </Alert>
    );
  }

  if (requirement.isError) {
    return (
      <Alert severity="error">
        <Typography>Cloud-optimized slides are not available for the selected slides.</Typography>
      </Alert>
    );
  }

  if (!requirement.isSatisfied) {
    return (
      <Alert severity="error">
        <Typography>Some selected slides are missing cloud-optimized slides.</Typography>
        {requirement.availableVersionSummary ? (
          <Typography variant="body2">Available versions: {requirement.availableVersionSummary}</Typography>
        ) : null}
        {size(requirement.missingSlideIds) > 0 ? (
          <Typography variant="body2">Missing slide IDs: {join(requirement.missingSlideIds, ', ')}</Typography>
        ) : null}
      </Alert>
    );
  }

  return (
    <Alert severity="success">
      <Typography>All selected slides have cloud-optimized slides.</Typography>
      {requirement.availableVersionSummary ? (
        <Typography variant="body2">Versions: {requirement.availableVersionSummary}</Typography>
      ) : null}
    </Alert>
  );
};

export const ExecutionRequirementsPanel = ({ requirements }: ExecutionRequirementsPanelProps): ReactElement | null => {
  if (size(requirements) === 0) {
    return null;
  }

  return (
    <Grid container direction="column" spacing={1}>
      {map(requirements, (requirement) => (
        <Grid item key={`${requirement.type}-${requirement.label}`}>
          <Typography variant="subtitle2" gutterBottom>
            {requirement.label}
          </Typography>
          {requirement.type === ExecutionRequirementType.CloudOptimizedSlides
            ? renderCloudOptimizedSlidesRequirement(requirement)
            : null}
        </Grid>
      ))}
    </Grid>
  );
};
